import {OnAll} from '@gongxigames/framework/lib/primitives/OnAll';
import {AtLeastOne} from '@gongxigames/framework/lib/primitives/AtLeastOne';
import {abortablePromise} from '@gongxigames/framework/lib/util/utilPromise';
import {CoreDI} from '@gongxigames/framework/core/configs/CoreDI';

export type EventMap = {[key: string]: any;};

type EventListener = {
    cb: (arg: any) => (void|Promise<void>);
    owner?: object;
    context?: any;
    once: boolean;
};

export type GetGameEvents<T> = T extends StrictEventEmitter<infer P> ? P : never;
export type EventArg<T extends EventMap, K extends keyof T> = T[K];
export type EventListenerFn<T extends EventMap, K extends keyof T> = (arg: EventArg<T, K>) => (void|Promise<any>);
export type EventAllListenerFn<T extends EventMap, K extends keyof T> = (arg: {[P in K]: EventArg<T, P>} ) => void;
export type EventOneOfListenerFn<T extends EventMap, K extends keyof T> = (arg: {[P in K]?: EventArg<T, P>} ) => void;
export type FunctionArgsTernary<T, A1 extends any[], A2 extends any[]> = T extends (undefined|void) ? A2 : A1;

export type SubscriptionOptions = {
    owner?: object;
    signal?: AbortSignal;
    context?: any;
    once?: boolean;
};

export type AsyncEmitOptions = {
    signal?: AbortSignal;
};

const ABORT_ERROR_MESSAGE = 'Can\'t subscribe to event: signal already aborted!';

export class StrictEventEmitter <T extends EventMap> {
    public static NAME: symbol = CoreDI.StrictEventEmitter;
    private static emitters: StrictEventEmitter<any>[] = [];

    static clearAll(): void {
        StrictEventEmitter.emitters.forEach((e) => e.clear());
    }

    private events: Map<keyof T, EventListener[]> = new Map();

    constructor() {
        StrictEventEmitter.emitters.push(this);
    }

    on<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>, options: SubscriptionOptions = {}): () => void {
        if (options.signal?.aborted) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }

        const off = () => this.off(eventName, cb);
        let list = this.events.get(eventName);

        if (!list) {
            list = [];
            this.events.set(eventName, list);
        }

        list.push({
            cb,
            once: !!options.once,
            owner: options.owner,
            context: options.context,
        });

        options.signal?.addEventListener('abort', off);

        return off;
    }

    once<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>, options: SubscriptionOptions = {}): () => void {
        return this.on(eventName, cb, {...options, once: true});
    }

    onceAll<K extends keyof T, P extends K[] >(eventNames: P, cb: EventAllListenerFn<T, P[number]>, options: SubscriptionOptions = {}): () => void {
        if (options?.signal?.aborted) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }

        const result = {} as any;
        const onAll = new OnAll();
        const offAllCallbacks: (() => void)[] = [];
        const offAll = () => offAllCallbacks.forEach(off => off());

        options.signal?.addEventListener('abort', offAll);

        for (const event of eventNames) {
            const off = this.on(event, onAll.cb(undefined, arg => {
                result[event] = arg;
            }), {...options, once: true});

            offAllCallbacks.push(off);
        }

        onAll.collect(() => {
            cb(result);
        });

        return offAll;
    }

    onceOneOf<K extends keyof T, P extends K[] >(eventNames: P, cb: EventOneOfListenerFn<T, P[number]>, options: SubscriptionOptions = {}): () => void {
        if (options?.signal?.aborted) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }

        const result = {} as any;
        const atLeastOne = new AtLeastOne();
        const offAllCallbacks: (() => void)[] = [];
        const offAll = () => offAllCallbacks.forEach(off => off());

        options.signal?.addEventListener('abort', offAll);

        for (const event of eventNames) {
            const off = this.on(event, atLeastOne.cb(undefined, arg => {
                result[event] = arg;
            }), {...options, once: true});

            offAllCallbacks.push(off);
        }

        atLeastOne.collect(() => {
            cb(result);
        });

        return offAll;
    }

    emit<
        K extends keyof T,
        V extends T[K]
    >(...args: FunctionArgsTernary<V, [key: K, val: V], [key: K]>): void {
        const [key, val] = args;
        const list = this.events.get(key);

        if (!list) {
            return;
        }

        for (const {cb, context} of list) {
            cb.call(context, val);
        }

        this.removeOneShotHandlers(list);
    }

    onAsync<K extends keyof T>(eventName: K, options?: SubscriptionOptions): Promise<EventArg<T, K>> {
        return new Promise(res => {
            this.on(eventName, res, options);
        });
    }

    onceAsync<K extends keyof T>(eventName: K, options?: SubscriptionOptions): Promise<EventArg<T, K>> {
        return new Promise(res => {
            this.once(eventName, res, options);
        });
    }

    onceAllAsync<K extends keyof T, P extends K[] >(eventNames: P, options?: SubscriptionOptions): Promise<{[P in K]: EventArg<T, P>}> {
        return new Promise(res => {
            this.onceAll(eventNames, res, options);
        });
    }

    onceOneOfAsync<K extends keyof T, P extends K[] >(eventNames: P, options?: SubscriptionOptions): Promise<{[P in K]?: EventArg<T, P>}> {
        return new Promise(res => {
            this.onceOneOf(eventNames, res, options);
        });
    }

    async emitAsync<
        K extends keyof T,
        V extends T[K]
    >(...args: FunctionArgsTernary<V, [key: K, val: V, options?: AsyncEmitOptions], [key: K, val?: undefined, options?: AsyncEmitOptions]>): Promise<void> {
        const [key, val, options] = args;
        const list = this.events.get(key);

        if (options?.signal?.aborted) {
            throw new Error(ABORT_ERROR_MESSAGE);
        }

        if (!list) {
            return;
        }

        const promises = list.map(({cb, context}) => cb.call(context, val));
        let promise = Promise.all(promises);

        if (options?.signal) {
            promise = abortablePromise(promise, options?.signal);
        }

        this.removeOneShotHandlers(list);

        await promise;
    }

    off<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): void {
        const list = this.events.get(eventName);

        if(!list) {
            return;
        }

        for (let i = 0; i < list.length; i++) {
            if(list[i].cb === cb) {
                list.splice(i, 1);
                i--;
            }
        }
    }

    offByOwner<K extends keyof T>(eventName: K, owner: object): void {
        const list = this.events.get(eventName);

        if(!list) {
            return;
        }

        for (let i = 0; i < list.length; i++) {
            if(list[i].owner === owner) {
                list.splice(i, 1);
                i--;
            }
        }
    }

    offAllByOwner(owner: object): void {
        for (const list of this.events.values()) {
            for (let i = 0; i < list.length; i++) {
                if(list[i].owner === owner) {
                    list.splice(i, 1);
                    i--;
                }
            }
        }
    }

    clear(): void {
        this.events.clear();
    }

    private removeOneShotHandlers(list: EventListener[]) {
        for (let i = 0; i < list.length; i++) {
            if (list[i].once) {
                list.splice(i, 1);
                i--;
            }
        }
    }
}